import { getSyntheticImageUrl } from '../../services/serviceWorker';
import { type ExamEvent } from '../exam-event';
import { type ExamSession } from '../exam-session';

import { type ExamFormInput } from './NewExamFormProvider';
import { type Exam } from './types';

export function mapExamRoomPaperItemsToFormInput(
  examRoomPaperItems: NonNullable<ExamSession['examRoomAnswer']>['examRoomExamPaperItems'],
) {
  if (!examRoomPaperItems) return {};
  const formInput: ExamFormInput = {};
  for (const examRoomPaperItem of examRoomPaperItems) {
    const { questionId, answer, isBookmark, examPaperItemId } = examRoomPaperItem;
    if (!questionId) continue;
    formInput[questionId] = {
      examPaperItemId,
      questionId,
      isBookmark,
      answer: answer.value,
    };
  }
  return formInput;
}

export function mapFormInputToExamRoomPaperItemsInput(formInput: ExamFormInput) {
  const latestAnswers = Object.values(formInput);
  return latestAnswers.map((latestAnswer) => {
    const { examPaperItemId, questionId, isBookmark, answer } = latestAnswer;
    return {
      examPaperItemId,
      questionId,
      isBookmark,
      answer: {
        value: answer,
        type: 'HTML',
      },
    };
  });
}

export function findCurrentExam(examEvent: ExamEvent, exam?: Exam) {
  if (!exam) return null;

  const targetExamEventIndex = examEvent.metadata.events.findIndex(
    (event) => event.examId === exam.id,
  );

  if (targetExamEventIndex === -1) return null;

  const targetExamEvent = examEvent.metadata.events[targetExamEventIndex];

  return {
    order: targetExamEvent.order,
    startedAt: targetExamEvent.startedAt ? new Date(targetExamEvent.startedAt) : null,
    endedAt: targetExamEvent.endedAt ? new Date(targetExamEvent.endedAt) : null,
  };
}

export function mapSignedUrlToImages(content: string, urlObject?: Record<string, string>) {
  if (!content || !urlObject) return content;
  const signedUrlsObj = urlObject;

  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/html');
  const images = doc.getElementsByTagName('img');

  for (const img of Array.from(images)) {
    const filename = img.getAttribute('content');
    if (filename && signedUrlsObj[filename]) {
      img.setAttribute('src', signedUrlsObj[filename]);
      img.removeAttribute('content');
    }
  }

  return doc.body.innerHTML;
}

export function mapSignedUrlToSyntheticUrls(
  content: string,
  questionId: string,
  urlObject?: Record<string, string>,
) {
  if (!content || !urlObject) return content;

  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/html');
  const images = doc.getElementsByTagName('img');

  for (const img of Array.from(images)) {
    const filename = img.getAttribute('content') || img.getAttribute('data-filename');

    // If no filename attribute, try to extract from src if it was already mapped
    let filenameToUse = filename;
    if (!filenameToUse) {
      const src = img.getAttribute('src');
      if (src) {
        // Find the filename by matching the signed URL
        const entry = Object.entries(urlObject).find(([, url]) => url === src);
        if (entry) {
          filenameToUse = entry[0];
        }
      }
    }

    if (filenameToUse && urlObject[filenameToUse]) {
      // Create synthetic URL using questionId/filename as the ID
      const imageId = `${questionId}/${filenameToUse}`;
      const syntheticUrl = getSyntheticImageUrl(imageId);

      img.setAttribute('src', syntheticUrl);
      // Store original filename for reference
      img.setAttribute('data-filename', filenameToUse);
      img.removeAttribute('content');
    }
  }

  return doc.body.innerHTML;
}
